import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  Lock,
  Eye,
  EyeSlash,
  EnvelopeSimple,
  Buildings,
  Spinner,
  UserPlus,
} from '@phosphor-icons/react'
import { getInviteByToken, acceptInvite } from '../services/users'
import api from '../services/api'
import { isExpired } from '../lib/date'

export default function AcceptInvitePage() {
  const navigate = useNavigate()
  const { isAuthenticated, setAuthData, user, logout } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(false)
  const [loadingInvite, setLoadingInvite] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteData, setInviteData] = useState<any>(null)
  const [success, setSuccess] = useState(false)

  // Registration form state
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phone: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    async function loadInvite() {
      if (!token) {
        setError('Token de convite invalido')
        setLoadingInvite(false)
        return
      }

      try {
        const data = await getInviteByToken(token)

        if (data.acceptedAt) {
          setError('Este convite ja foi aceito')
        } else if (isExpired(data.expiresAt)) {
          setError('Este convite expirou')
        } else {
          setInviteData(data)
        }
      } catch (error: any) {
        setError(error.response?.data?.error || 'Convite nao encontrado')
      } finally {
        setLoadingInvite(false)
      }
    }

    loadInvite()
  }, [token])

  // Handle accept for logged-in users
  async function handleAcceptInvite() {
    if (!token) return

    try {
      setLoading(true)
      setError(null)
      await acceptInvite(token)

      setSuccess(true)
    } catch (error: any) {
      setError(error.response?.data?.error || 'Erro ao aceitar convite')
    } finally {
      setLoading(false)
    }
  }

  // Handle signup + accept for new users
  async function handleSignupWithInvite(e: React.FormEvent) {
    e.preventDefault()

    if (!token) return

    // Validation
    if (!formData.name.trim()) {
      setError('Digite seu nome')
      return
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas nao conferem')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await api.post('/auth/signup-with-invite', {
        token,
        name: formData.name,
        password: formData.password,
        phone: formData.phone || undefined,
      })

      // Auto-login with received tokens
      if (response.data.access_token) {
        // Store tokens
        localStorage.setItem('access_token', response.data.access_token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
        // Set user in context
        setAuthData(response.data.user)
        setSuccess(true)
      } else if (response.data.requiresLogin) {
        // Fallback: redirect to login
        navigate('/login')
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Erro ao criar conta')
    } finally {
      setLoading(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} className="text-emerald-600" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {isAuthenticated ? 'Convite Aceito!' : 'Conta Criada com Sucesso!'}
              </h2>
              <p className="text-gray-600 mb-8">
                Voce agora faz parte de <span className="font-semibold text-gray-900">{inviteData?.companyName}</span>.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
              >
                Acessar Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state (no invite data)
  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle size={48} className="text-red-500" weight="duotone" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Convite Invalido</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/login')} variant="outline">
                Ir para Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Loading state
  if (loadingInvite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-16 pb-16">
            <div className="text-center">
              <Spinner size={56} className="mx-auto text-emerald-600 mb-6 animate-spin" />
              <p className="text-gray-600">Validando convite...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!inviteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-16 pb-16">
            <div className="text-center">
              <XCircle size={64} className="mx-auto text-red-500 mb-4" weight="duotone" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Convite Invalido</h2>
              <p className="text-gray-600 mb-6">Convite nao encontrado ou expirado</p>
              <Button onClick={() => navigate('/login')} variant="outline">
                Ir para Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // If user is authenticated, show simple accept screen
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-emerald-600" weight="duotone" />
            </div>
            <CardTitle className="text-2xl">Voce foi convidado!</CardTitle>
            <CardDescription>
              Aceite o convite para se juntar a equipe
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="bg-gray-50 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Buildings size={20} className="text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Empresa</p>
                  <p className="font-semibold text-gray-900">{inviteData.companyName}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{inviteData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Funcao:</span>
                  <span className="font-medium text-gray-900 capitalize">{inviteData.role}</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <XCircle size={18} />
                {error}
              </div>
            )}

            {user?.email && inviteData.email &&
             user.email.toLowerCase() !== String(inviteData.email).toLowerCase() ? (
              <div className="space-y-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  Este convite é para <strong>{inviteData.email}</strong>, mas você está logado como <strong>{user.email}</strong>. Saia e entre (ou cadastre) com o email convidado para aceitar.
                </div>
                <Button
                  onClick={async () => { await logout(); }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                >
                  Sair e usar o email convidado
                </Button>
                <Button onClick={() => navigate('/')} variant="outline" className="w-full h-12">
                  Cancelar
                </Button>
              </div>
            ) : (
            <div className="space-y-3">
              <Button
                onClick={handleAcceptInvite}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
              >
                {loading ? (
                  <>
                    <Spinner size={20} className="animate-spin mr-2" />
                    Aceitando...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} className="mr-2" weight="bold" />
                    Aceitar Convite
                  </>
                )}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full h-12"
              >
                Cancelar
              </Button>
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  // Not authenticated - show registration form
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl border-0 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-8 py-8 text-center text-white">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <UserPlus size={36} className="text-white" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Crie sua Conta</h1>
          <p className="text-emerald-100 text-sm">
            Voce foi convidado para {inviteData.companyName}
          </p>
        </div>

        <CardContent className="p-8">
          {/* Invite details */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Buildings size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Empresa</p>
                <p className="font-semibold text-gray-900">{inviteData.companyName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <EnvelopeSimple size={16} />
              <span>{inviteData.email}</span>
              <span className="text-gray-400">|</span>
              <span className="capitalize">{inviteData.role}</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm flex items-center gap-3">
              <XCircle size={20} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Registration form */}
          <form onSubmit={handleSignupWithInvite} className="space-y-5">
            <div>
              <Label htmlFor="name" className="flex items-center gap-2 text-gray-700 mb-2">
                <User size={16} className="text-gray-400" />
                Nome Completo
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Seu nome completo"
                className="h-12"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2 text-gray-700 mb-2">
                <EnvelopeSimple size={16} className="text-gray-400" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                className="h-12 bg-gray-100"
                disabled
              />
              <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Email vinculado ao convite
              </p>
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center gap-2 text-gray-700 mb-2">
                <Lock size={16} className="text-gray-400" />
                Senha
                <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimo 6 caracteres"
                  className="h-12 pr-12"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="flex items-center gap-2 text-gray-700 mb-2">
                <Lock size={16} className="text-gray-400" />
                Confirmar Senha
                <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Repita a senha"
                  className="h-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold shadow-lg shadow-emerald-200"
              >
                {loading ? (
                  <>
                    <Spinner size={20} className="animate-spin mr-2" />
                    Criando conta...
                  </>
                ) : (
                  <>
                    <UserPlus size={20} className="mr-2" weight="bold" />
                    Criar Conta e Aceitar Convite
                  </>
                )}
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Ja tem uma conta?{' '}
                  <button
                    type="button"
                    onClick={() => navigate(`/login?returnTo=${encodeURIComponent(`/accept-invite?token=${token}`)}`)}
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Faca login
                  </button>
                </p>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
